import { GenericScaledField } from 'src/components/controls/rhf/GenericScaledField';
import { filterAndSortAntennas } from 'src/pages/antennas/antenna-utils';
import { meterToAny, range, round } from 'src/utils/useful_functions';
import LimitControl from '../LimitControl';
import {
  PTPChoices,
  PTPFormField,
  PTPParams,
  PTPShowProps,
  defaultLkShared,
  isChoicesComplex,
} from '../utils';
import AntennaSelection from 'src/pages/equipment/common/AntennaSelection';
import { get, isNumber } from 'lodash';
import {
  getAntennasFromChoices,
  getEquipmentFromChoices,
  getEndFromChoices,
} from '../utils';
import { store } from 'src/store';
import { linkKindsMultiband } from '../link-kind-utils';
import { CUSTOM_ANTENNA_OTHER } from 'src/app.constants';
import {
  reset,
  setAntennaPolarities,
} from 'src/pages/antennas/antenna.reducer';

export function genericLkRadioGetters(
  endName: string,
  n: number,
  keyedPaths: Record<string, string>
) {
  return Object.fromEntries(
    Object.entries(keyedPaths).map(([attr, path]) => [
      attr,
      range(n).map((i) => `${endName}.radios.${i}.${path}`),
    ])
  );
}

export function genericLkAntennaGetters(
  endName: string,
  n: number,
  keyedPaths: Record<string, string>
) {
  return Object.fromEntries(
    Object.entries(keyedPaths).map(([attr, path]) => [
      attr,
      range(n).map((i) => `${endName}.radios.${i}.antennas.0.${path}`),
    ])
  );
}

function genericHotStandbySync(
  form: any,
  getter: any,
  setter: any,
  attr: string
) {
  return (endName: string, to: number, from_: number) => {
    setter(
      form,
      `${endName}.radios.${to}.antennas.0.${attr}`,
      getter(form, `${endName}.radios.${from_}.antennas.0.${attr}`)
    );
  };
}

function genericPowerSync(form: any, getter: any, setter: any) {
  return (endName: string, attr: string, to: number, from_: number) => {
    setter(
      form,
      `${endName}.radios.${to}.power.${attr}`,
      getter(form, `${endName}.radios.${from_}.power.${attr}`)
    );
  };
}

type ExtraAntennaOptions = {
  label: string;
  show: any;
  getter: string;
};

export function genericAntennaControl(
  endName: string,
  extraOpts?: ExtraAntennaOptions
) {
  let label;
  let show;
  let getter;
  let key;

  if (extraOpts) {
    label = extraOpts.label;
    show = extraOpts.show;
    getter = extraOpts.getter;
    // use the label otherwise it wont rerender the first antenna correctly
    key = `_${label}`;
  } else {
    label = 'Antenna';
    getter = `${endName}.radios.0.antennas.0.lp_antenna_id`;
    key = '';
    // no 'show' required
  }

  return {
    attrName: `${endName}_antenna${key}`,
    getter,
    refreshesChoices: true,
    gridArea: 'antenna',
    component: AntennaSelection,
    componentProps: ({ choices, formGetter, pathIndex = 0 }) => {
      const lk = getEquipmentFromChoices(choices)?.link_protection?.value;
      const isMultiband = linkKindsMultiband.includes(lk);

      // Multiband uses a single antenna sourced from Link A (radio 0): the
      // stored value is radio 0's antenna id and the selector matches by id.
      // But the gain must reflect each band's frequency, so for Link B we take
      // that band's antenna list (correct center_frequency / gains) and remap
      // each entry's id to radio 0's id for the same part. That way the value
      // still matches while the gain shown is the per-band value. Antenna
      // protection also stays per band (co-polar's Common Antenna - Symmetric
      // Coupling), handled via pathIndex below.
      const partId = (a: any) => a?.data?.motono ?? a?.data?.display_partno;
      let antennas: any[];
      if (isMultiband && pathIndex > 0) {
        const radio0IdByPart: Record<string, string> = {};
        getAntennasFromChoices(choices, endName, 0).forEach((a: any) => {
          const key = partId(a);
          if (key != null) {
            radio0IdByPart[key] = a.id;
          }
        });
        antennas = getAntennasFromChoices(choices, endName, pathIndex).map(
          (a: any) => {
            const radio0Id = radio0IdByPart[partId(a)];
            return radio0Id != null ? { ...a, id: radio0Id } : a;
          }
        );
      } else {
        // Copy the antenna array so that we don't modify the original array if
        // we insert the custom antenna "Other..." option.
        antennas = [...getAntennasFromChoices(choices, endName, pathIndex)];
      }
      const supported_polarities = getEndFromChoices(
        choices,
        endName,
        pathIndex
      ).supported_polarities;
      // Use optional chaining: while the choices refresh (e.g. switching link
      // type) a path can be momentarily absent, and indexing into it directly
      // would throw and blank the whole antenna control.
      let end =
        (isChoicesComplex(choices)
          ? choices.paths?.[pathIndex]?.[endName]?.end
          : choices[endName]?.end) ?? {};

      if (end.allow_custom_antennas) {
        antennas.push(CUSTOM_ANTENNA_OTHER);
        store.dispatch(
          setAntennaPolarities({
            polarities: supported_polarities,
          })
        );
      }

      const endChoices = getEndFromChoices(choices, endName, pathIndex);
      return {
        choices: antennas,
        kind: 'ptp',
        cssClass: endName === 'remote' ? 'right-align' : '',
        value: formGetter(getter),
        antennaProtection: endChoices.antenna_protection,
        antennaProtectionChoices: endChoices.antenna_protection_choices,
        antennaProtectionLabels: endChoices.antenna_protection_labels,
        protectionGetter: `${endName}.radios.0.antennas.0.config.antenna_protection`,
        // On multiband the antenna sits inside per-band tabs, so keep the
        // "Antenna" field label even when the antenna-protection label is also
        // shown (e.g. co-polar's "Common Antenna - Symmetric Coupling").
        showAntennaLabel: isMultiband,
      };
    },
    label,
    show,
    nextValue: makeAntennaNextValue(endName),
    nextValueExtra(newChoices, getValues, setValue) {
      const end = getEndFromChoices(newChoices, endName);
      const getter = `${endName}.radios.0.antennas.0.config.antenna_protection`;
      const currentProt = getValues(getter);
      if (
        !end.antenna_protection_choices?.includes(currentProt) &&
        end.antenna_protection != null
      ) {
        setValue(getter, end.antenna_protection, { shouldDirty: true });
      }
    },
    warning({ path }: PTPParams): string {
      const warning = path.warnings[endName]?.antenna;
      if (warning != null && warning.kind === 'warning') {
        return warning.message;
      }
      return null;
    },
    warningProps({ modified }: any): any {
      if (modified) {
        return { basic: true, color: 'blue' };
      }
      return {};
    },
    info({ path }: PTPParams): string {
      const info = path.warnings[endName]?.antenna;
      if (info != null && info.kind === 'info') {
        return info.message;
      }
      return null;
    },
    lk: {
      shared: defaultLkShared,
      unlicensed_1plus1: {
        getter({ lk, pathIndex, formGetter }) {
          const prot = formGetter(
            `${endName}.radios.0.antennas.0.config.antenna_protection`
          );

          if (lk === 'unlicensed_1plus1' && prot !== 'Redundant Antennas') {
            return `${endName}.radios.0.antennas.0.lp_antenna_id`;
          }

          return `${endName}.radios.${pathIndex}.antennas.0.lp_antenna_id`;
        },
        finalSync(form, getter, setter) {
          const sync = genericHotStandbySync(
            form,
            getter,
            setter,
            'config.antenna_protection'
          );
          sync('local', 1, 0);
          sync('remote', 1, 0);
        },
      },
      '2plus0': {
        finalSync(form, getter, setter) {
          const sync = genericHotStandbySync(
            form,
            getter,
            setter,
            'config.antenna_protection'
          );
          sync('local', 1, 0);
          sync('remote', 1, 0);
        },
      },
      multiband: {
        // The antenna is a single selection shared by every band, sourced from
        // Link A (radio 0). Each band's gain is derived from that antenna at
        // its own frequency, so every tab binds to radio 0. (Three entries
        // cover multiband 2+0's Link A / B1 / B2; 1+0 uses the first two.)
        getter: [
          `${endName}.radios.0.antennas.0.lp_antenna_id`,
          `${endName}.radios.0.antennas.0.lp_antenna_id`,
          `${endName}.radios.0.antennas.0.lp_antenna_id`,
        ],
        finalSync(form, getter, setter) {
          // Mirror the single antenna (and its protection) from Link A onto
          // every band before saving so each radio references it. Iterate the
          // real radios so this is correct for 1+0 (2 radios) and 2+0
          // (3 radios) without creating phantom radios.
          for (const end of ['local', 'remote']) {
            const radios = getter(form, `${end}.radios`) ?? [];
            for (let i = 1; i < radios.length; i++) {
              setter(
                form,
                `${end}.radios.${i}.antennas.0.lp_antenna_id`,
                getter(form, `${end}.radios.0.antennas.0.lp_antenna_id`)
              );
              setter(
                form,
                `${end}.radios.${i}.antennas.0.config.antenna_protection`,
                getter(
                  form,
                  `${end}.radios.0.antennas.0.config.antenna_protection`
                )
              );
            }
          }
        },
      },
      '1plus1': {
        getter({ lk, pathIndex, formGetter }) {
          const prot = formGetter(
            `${endName}.radios.0.antennas.0.config.antenna_protection`
          );

          if (prot !== 'Spatial Diversity') {
            return `${endName}.radios.0.antennas.0.lp_antenna_id`;
          }

          return `${endName}.radios.${pathIndex}.antennas.0.lp_antenna_id`;
        },
        finalSync(form, getter, setter) {
          const sync = genericHotStandbySync(
            form,
            getter,
            setter,
            'config.antenna_protection'
          );

          sync('local', 1, 0);
          sync('local', 2, 0);
          sync('local', 3, 0);
          sync('remote', 1, 0);
          sync('remote', 2, 0);
          sync('remote', 3, 0);
        },
      },
    },
  };
}

function hotStandbyAntennaHeightGetter(endName: string) {
  return ({ lk, pathIndex, formGetter }: any) => {
    const prot = formGetter(
      `${endName}.radios.0.antennas.0.config.antenna_protection`
    );

    if (
      (lk === 'hot_standby' || lk === 'unit_redundancy') &&
      prot !== 'Spatial Diversity'
    ) {
      return `${endName}.radios.0.antennas.0.height`;
    }

    if (
      lk === 'unlicensed_1plus1' &&
      prot === 'Common Antenna - Symmetric Coupling'
    ) {
      return `${endName}.radios.0.antennas.0.height`;
    }

    return `${endName}.radios.${pathIndex}.antennas.0.height`;
  };
}

export function genericAntennaHeight(endName: string, extraOpts?: any) {
  let show;
  let getter;
  let key;
  let nextValue;

  if (extraOpts) {
    show = extraOpts.show;
    getter = extraOpts.getter;
    key = '_diverse';
    nextValue = (currentValue, newChoices) => {
      if (currentValue == null) {
        // link kind change: 2+X -> 2+X SD
        const end = getEndFromChoices(newChoices, endName);
        if (end == null) {
          // just in case, have a default
          return 10;
        }
        return end.default_diverse_antenna_height;
      }
    };
  } else {
    // no 'show' required
    getter = `${endName}.radios.0.antennas.0.height`;
    key = '';
    nextValue = null;
  }

  return {
    label: 'Antenna Height',
    attrName: `${endName}_antennaHeight${key}`,
    getter,
    show,
    warning({ formGetter }) {
      return formGetter(`${endName}.radios.0.warnings.antenna_height`);
    },
    component: GenericScaledField,
    precision: 1,
    min: 0,
    max: 3000,
    useHeightPrefs: true,
    nextValue,
    gridArea: 'antenna',
    lk: {
      shared: ({ lk, choices, endName }) => {
        const end = getEndFromChoices(choices, endName);
        const protection = end?.antenna_protection;

        if (
          lk === 'unlicensed_2plus0' &&
          (protection === 'Spatial Diversity' ||
            protection === 'Redundant Antennas')
        ) {
          return false;
        }

        return defaultLkShared({ lk });
      },
      '1plus1': {
        getter: hotStandbyAntennaHeightGetter(endName),
        finalSync(form, getter, setter) {
          const sync = genericHotStandbySync(form, getter, setter, 'height');
          sync('local', 1, 0);
          sync('local', 3, 2);
          sync('remote', 2, 0);
          sync('remote', 3, 1);
        },
      },
      unlicensed_1plus1: {
        getter: hotStandbyAntennaHeightGetter(endName),
      },
      '2plus0': genericLkAntennaGetters(endName, 2, {
        getter: 'height',
      }),
      multiband: {
        // Antenna height is a single value shared by every band (same mast;
        // Link A / radio 0 is authoritative in the backend), so every band's
        // tab binds to radio 0 - mirrored, like the antenna. Three entries
        // cover multiband 2+0 (Link A / B1 / B2); 1+0 uses the first two.
        getter: [
          `${endName}.radios.0.antennas.0.height`,
          `${endName}.radios.0.antennas.0.height`,
          `${endName}.radios.0.antennas.0.height`,
        ],
      },
    },
    componentProps: ({
      path,
      formGetter,
      formSetter,
      refreshChoices,
      units,
    }: PTPShowProps) => {
      if (path == null) {
        return {};
      }

      const siteMaxHeight = path[endName]?.site?.maximum_height;

      let result: any = {
        unitExtraTooltip: ({ units }) => {
          let height: number;
          if (units !== 'm') {
            height = meterToAny(siteMaxHeight, units, 1, false);
          } else {
            height = siteMaxHeight;
          }
          return ` (Max height at site is ${height.toFixed(1)}${units})`;
        },
        onAcceptChange: (evt) => {
          const value = formGetter(`${endName}.radios.0.antennas.0.height`);
          if (value === '' || !isNumber(value)) {
            // This doesn't refresh the value in the UI so the
            // user still has to fix the height before saving.
            // It does allow refreshChoices to return without errors.
            formSetter(`${endName}.radios.0.antennas.0.height`, 0, {
              shouldDirty: true,
            });
          }
          refreshChoices({ field: { attrName: `${endName}_antennaHeight` } });
        },
      };

      let formHeight = formGetter(getter);
      if (typeof formHeight === 'string') {
        formHeight = parseFloat(formHeight);
      }

      // Compare the value as it is shown to the user (1 dp, in their preferred
      // units) rather than the raw stored metres. Otherwise a value that rounds
      // to within the site maximum for display is still flagged red.
      const asDisplayed = (metres: number) => {
        const converted =
          units != null && units !== 'm'
            ? meterToAny(metres, units, 1, false)
            : metres;
        return parseFloat(converted.toFixed(1));
      };

      if (
        siteMaxHeight != null &&
        asDisplayed(formHeight) > asDisplayed(siteMaxHeight)
      ) {
        result.textColor = 'red';
      }

      return result;
    },
  };
}

function maxEirpGetters_1plus1(endName: string) {
  if (endName === 'local') {
    return {
      maxValueGetter: [
        `${endName}.radios.0.power.eirp`,
        `${endName}.radios.0.power.eirp`,
        `${endName}.radios.2.power.eirp`,
        `${endName}.radios.2.power.eirp`,
      ],
      checkboxGetter: [
        `${endName}.radios.0.power.use_user_eirp`,
        `${endName}.radios.0.power.use_user_eirp`,
        `${endName}.radios.2.power.use_user_eirp`,
        `${endName}.radios.2.power.use_user_eirp`,
      ],
      valueGetter: [
        `${endName}.radios.0.power.user_eirp`,
        `${endName}.radios.0.power.user_eirp`,
        `${endName}.radios.2.power.user_eirp`,
        `${endName}.radios.2.power.user_eirp`,
      ],
    };
  } else {
    return {
      maxValueGetter: [
        `${endName}.radios.0.power.eirp`,
        `${endName}.radios.1.power.eirp`,
        `${endName}.radios.0.power.eirp`,
        `${endName}.radios.1.power.eirp`,
      ],
      checkboxGetter: [
        `${endName}.radios.0.power.use_user_eirp`,
        `${endName}.radios.1.power.use_user_eirp`,
        `${endName}.radios.0.power.use_user_eirp`,
        `${endName}.radios.1.power.use_user_eirp`,
      ],
      valueGetter: [
        `${endName}.radios.0.power.user_eirp`,
        `${endName}.radios.1.power.user_eirp`,
        `${endName}.radios.0.power.user_eirp`,
        `${endName}.radios.1.power.user_eirp`,
      ],
    };
  }
}

export function genericMaxEirp(endName: string) {
  return {
    label: 'User Limit',
    maxValueLabel: ({ formGetter }) => {
      const product = formGetter('local.radios.0.equipment.product');
      if (getPTP450VPowerInfo(formGetter)) {
        return 'Maximum EIRP (CC)';
      } else if (isPTP450v(product)) {
        return 'Maximum EIRP (Carrier)';
      } else {
        return 'Maximum EIRP';
      }
    },
    attrName: `${endName}_eirp_limit`,
    component: LimitControl,
    maxValueGetter: `${endName}.radios.0.power.eirp`,
    maxValueFormatter: (value) => round(parseFloat(value), 1),
    checkboxGetter: `${endName}.radios.0.power.use_user_eirp`,
    valueGetter: `${endName}.radios.0.power.user_eirp`,
    units: 'dBm',
    precision: 1,
    min: -10,
    max: 90,
    reCalculate: true,
    lk: {
      shared: ({ lk, choices }) => {
        if (
          lk === 'carrier_1_cc_2' ||
          lk === 'carrier_2_cc_2' ||
          lk === 'carrier_2_cc_3'
        ) {
          const band1 = get(choices, 'paths.0.equipment.band.value');
          const band2 = get(choices, 'paths.1.equipment.band.value');
          return lk === 'carrier_1_cc_2' || band1 === band2;
        }
        return false;
      },
      unlicensed_1plus1: genericLkRadioGetters(endName, 2, {
        maxValueGetter: 'power.eirp',
        checkboxGetter: 'power.use_user_eirp',
        valueGetter: 'power.user_eirp',
      }),
      carrier_1_cc_2: {
        ...genericLkRadioGetters(endName, 2, {
          maxValueGetter: 'power.eirp',
          checkboxGetter: 'power.use_user_eirp',
          valueGetter: 'power.user_eirp',
        }),
        finalSync(form, getter, setter) {
          const sync = genericPowerSync(form, getter, setter);
          sync('local', 'use_user_eirp', 1, 0);
          sync('local', 'user_eirp', 1, 0);
          sync('remote', 'use_user_eirp', 1, 0);
          sync('remote', 'user_eirp', 1, 0);
        },
      },
      carrier_2_cc_2: {
        ...genericLkRadioGetters(endName, 2, {
          maxValueGetter: 'power.eirp',
          checkboxGetter: 'power.use_user_eirp',
          valueGetter: 'power.user_eirp',
        }),
        finalSync(form, getter, setter) {
          const sync = genericPowerSync(form, getter, setter);
          const conditionalSync = (endName, attr, to, from_) => {
            const band1 = getter(form, `${endName}.radios.0.equipment.band`);
            const band2 = getter(form, `${endName}.radios.1.equipment.band`);
            if (band1 === band2) {
              sync(endName, attr, to, from_);
            }
          };

          conditionalSync('local', 'use_user_eirp', 1, 0);
          conditionalSync('local', 'user_eirp', 1, 0);
          conditionalSync('remote', 'use_user_eirp', 1, 0);
          conditionalSync('remote', 'user_eirp', 1, 0);
        },
      },
      carrier_2_cc_3: {
        maxValueGetter: [
          `${endName}.radios.0.power.eirp`,
          `${endName}.radios.1.power.eirp`,
          `${endName}.radios.0.power.eirp`, // CC3 uses CC1's getter (always synced)
        ],
        checkboxGetter: [
          `${endName}.radios.0.power.use_user_eirp`,
          `${endName}.radios.1.power.use_user_eirp`,
          `${endName}.radios.0.power.use_user_eirp`, // CC3 uses CC1's getter (always synced)
        ],
        valueGetter: [
          `${endName}.radios.0.power.user_eirp`,
          `${endName}.radios.1.power.user_eirp`,
          `${endName}.radios.0.power.user_eirp`, // CC3 uses CC1's getter (always synced)
        ],
        afterOnChangeCheckbox(checked, { setValue, getValues }) {
          // Sync CC1's checkbox state to CC3 (always in same band)
          setValue(`${endName}.radios.2.power.use_user_eirp`, checked, {
            shouldValidate: true,
          });

          // Sync CC1's checkbox state to CC2 only if bands match
          const band1 = getValues(`${endName}.radios.0.equipment.band`);
          const band2 = getValues(`${endName}.radios.1.equipment.band`);
          if (band1 === band2) {
            setValue(`${endName}.radios.1.power.use_user_eirp`, checked, {
              shouldValidate: true,
            });
          }
        },
        afterOnChange(newValue, { setValue, getValues }) {
          // Sync CC1's user_eirp value to CC3 (always in same band)
          setValue(`${endName}.radios.2.power.user_eirp`, newValue, {
            shouldValidate: true,
          });

          // Sync CC1's user_eirp value to CC2 only if bands match
          const band1 = getValues(`${endName}.radios.0.equipment.band`);
          const band2 = getValues(`${endName}.radios.1.equipment.band`);
          if (band1 === band2) {
            setValue(`${endName}.radios.1.power.user_eirp`, newValue, {
              shouldValidate: true,
            });
          }
        },
        finalSync(form, getter, setter) {
          const sync = genericPowerSync(form, getter, setter);

          // Sync CC1 (radio 0) to CC3 (radio 2) - always in same band
          ['local', 'remote'].forEach((end) => {
            sync(end, 'use_user_eirp', 2, 0);
            sync(end, 'user_eirp', 2, 0);
          });

          // Sync CC1 to CC2 only if same band
          ['local', 'remote'].forEach((end) => {
            const band1 = getter(form, `${end}.radios.0.equipment.band`);
            const band2 = getter(form, `${end}.radios.1.equipment.band`);
            if (band1 === band2) {
              sync(end, 'use_user_eirp', 1, 0);
              sync(end, 'user_eirp', 1, 0);
            }
          });
        },
      },
      '2plus0': genericLkRadioGetters(endName, 2, {
        maxValueGetter: 'power.eirp',
        checkboxGetter: 'power.use_user_eirp',
        valueGetter: 'power.user_eirp',
      }),
      multiband: genericLkRadioGetters(endName, 3, {
        maxValueGetter: 'power.eirp',
        checkboxGetter: 'power.use_user_eirp',
        valueGetter: 'power.user_eirp',
      }),
      '2plus2': genericLkRadioGetters(endName, 2, {
        maxValueGetter: 'power.eirp',
        checkboxGetter: 'power.use_user_eirp',
        valueGetter: 'power.user_eirp',
      }),
      '4plus0': genericLkRadioGetters(endName, 4, {
        maxValueGetter: 'power.eirp',
        checkboxGetter: 'power.use_user_eirp',
        valueGetter: 'power.user_eirp',
      }),
      '1plus1': {
        ...maxEirpGetters_1plus1(endName),
        finalSync(form, getter, setter) {
          const sync = genericPowerSync(form, getter, setter);
          sync('local', 'use_user_eirp', 1, 0);
          sync('local', 'user_eirp', 1, 0);
          sync('local', 'use_user_eirp', 3, 2);
          sync('local', 'user_eirp', 3, 2);
          sync('remote', 'use_user_eirp', 2, 0);
          sync('remote', 'user_eirp', 2, 0);
          sync('remote', 'use_user_eirp', 3, 1);
          sync('remote', 'user_eirp', 3, 1);
        },
      },
    },
    warning({ path, choices }: PTPParams): string {
      if (choices) {
        const end = getEndFromChoices(choices, endName);
        if (end?.hasOwnProperty('diverse_eirp_warning')) {
          return end.diverse_eirp_warning;
        }
        if (end?.hasOwnProperty('eirp_warning')) {
          return end.eirp_warning;
        }
      }
      return path[endName]?.radios[0].warnings.eirp;
    },
    warningProps({ modified }: any): any {
      if (modified) {
        return { basic: true, color: 'blue' };
      }
      return {};
    },
  };
}

function maxPowerGetters_1plus1(endName: string) {
  if (endName === 'local') {
    return {
      maxValueGetter: [
        `${endName}.radios.0.power.power`,
        `${endName}.radios.0.power.power`,
        `${endName}.radios.2.power.power`,
        `${endName}.radios.2.power.power`,
      ],
      checkboxGetter: [
        `${endName}.radios.0.power.use_user_power`,
        `${endName}.radios.0.power.use_user_power`,
        `${endName}.radios.2.power.use_user_power`,
        `${endName}.radios.2.power.use_user_power`,
      ],
      valueGetter: [
        `${endName}.radios.0.power.user_power`,
        `${endName}.radios.0.power.user_power`,
        `${endName}.radios.2.power.user_power`,
        `${endName}.radios.2.power.user_power`,
      ],
    };
  } else {
    return {
      maxValueGetter: [
        `${endName}.radios.0.power.power`,
        `${endName}.radios.1.power.power`,
        `${endName}.radios.0.power.power`,
        `${endName}.radios.1.power.power`,
      ],
      checkboxGetter: [
        `${endName}.radios.0.power.use_user_power`,
        `${endName}.radios.1.power.use_user_power`,
        `${endName}.radios.0.power.use_user_power`,
        `${endName}.radios.1.power.use_user_power`,
      ],
      valueGetter: [
        `${endName}.radios.0.power.user_power`,
        `${endName}.radios.1.power.user_power`,
        `${endName}.radios.0.power.user_power`,
        `${endName}.radios.1.power.user_power`,
      ],
    };
  }
}

const isPTP450v = (product: string): boolean => {
  return product.startsWith('PTP 450v');
};

const getPTP450VPowerInfo = (
  formGetter: any,
  pathIndex: number = 0
): string | null => {
  const product = formGetter('local.radios.0.equipment.product');
  const lk = formGetter('local.radios.0.equipment.link_protection');

  if (
    !isPTP450v(product) ||
    !formGetter('local.radios.1') ||
    lk === 'carrier_1_cc_1'
  ) {
    return null;
  }

  const msg = `Tx power is equally distributed between the CCs when the carriers are in the same band.`;
  const band1 = formGetter('local.radios.0.equipment.band');
  const band2 = formGetter('local.radios.1.equipment.band');
  const bandsMatch = !band2 || band2 === band1;

  // carrier_1_cc_2: Single carrier, both CCs always in same band
  if (lk === 'carrier_1_cc_2') {
    return msg;
  }

  // carrier_2_cc_3: Two carriers, 3 CCs total
  // CC1 (pathIndex 0) and CC3 (pathIndex 2) from carrier 1 - always same band
  // CC2 (pathIndex 1) from carrier 2 - only synced when bands match
  if (lk === 'carrier_2_cc_3') {
    return pathIndex === 1 ? (bandsMatch ? msg : null) : msg;
  }

  // Other multi-CC link kinds: show message only when bands match
  return bandsMatch ? msg : null;
};

export function genericMaxPower(endName: string) {
  return {
    label: 'User Limit',
    maxValueLabel: ({ formGetter }) => {
      const product = formGetter('local.radios.0.equipment.product');
      if (getPTP450VPowerInfo(formGetter)) {
        return 'Maximum Power (CC)';
      } else if (isPTP450v(product)) {
        return 'Maximum Power (Carrier)';
      } else {
        return 'Maximum Power';
      }
    },
    component: LimitControl,
    attrName: `${endName}_power_limit`,
    maxValueGetter: `${endName}.radios.0.power.power`,
    maxValueFormatter: (value) => round(parseFloat(value), 1),
    units: 'dBm',
    precision: 1,
    checkboxGetter: `${endName}.radios.0.power.use_user_power`,
    valueGetter: `${endName}.radios.0.power.user_power`,
    min: -40,
    max: 40,
    reCalculate: true,
    info: ({ formGetter, pathIndex }) => {
      return getPTP450VPowerInfo(formGetter, pathIndex);
    },
    infoColor: 'yellow',
    lk: {
      shared: ({ lk, choices }) => {
        if (
          lk === 'carrier_1_cc_2' ||
          lk === 'carrier_2_cc_2' ||
          lk === 'carrier_2_cc_3'
        ) {
          const band1 = get(choices, 'paths.0.equipment.band.value');
          const band2 = get(choices, 'paths.1.equipment.band.value');
          return lk === 'carrier_1_cc_2' || band1 === band2;
        }
        return false;
      },
      unlicensed_1plus1: genericLkRadioGetters(endName, 2, {
        maxValueGetter: 'power.power',
        checkboxGetter: 'power.use_user_power',
        valueGetter: 'power.user_power',
      }),
      carrier_1_cc_2: {
        ...genericLkRadioGetters(endName, 2, {
          maxValueGetter: 'power.power',
          checkboxGetter: 'power.use_user_power',
          valueGetter: 'power.user_power',
        }),
        finalSync(form, getter, setter) {
          const sync = genericPowerSync(form, getter, setter);
          sync('local', 'use_user_power', 1, 0);
          sync('local', 'user_power', 1, 0);
          sync('remote', 'use_user_power', 1, 0);
          sync('remote', 'user_power', 1, 0);
        },
      },
      carrier_2_cc_2: {
        ...genericLkRadioGetters(endName, 2, {
          maxValueGetter: 'power.power',
          checkboxGetter: 'power.use_user_power',
          valueGetter: 'power.user_power',
        }),
        finalSync(form, getter, setter) {
          const sync = genericPowerSync(form, getter, setter);
          const conditionalSync = (endName, attr, to, from_) => {
            const band1 = getter(form, `${endName}.radios.0.equipment.band`);
            const band2 = getter(form, `${endName}.radios.1.equipment.band`);
            if (band1 === band2) {
              sync(endName, attr, to, from_);
            }
          };

          conditionalSync('local', 'use_user_power', 1, 0);
          conditionalSync('local', 'user_power', 1, 0);
          conditionalSync('remote', 'use_user_power', 1, 0);
          conditionalSync('remote', 'user_power', 1, 0);
        },
      },
      carrier_2_cc_3: {
        maxValueGetter: [
          `${endName}.radios.0.power.power`,
          `${endName}.radios.1.power.power`,
          `${endName}.radios.0.power.power`, // CC3 uses CC1's getter (always synced)
        ],
        checkboxGetter: [
          `${endName}.radios.0.power.use_user_power`,
          `${endName}.radios.1.power.use_user_power`,
          `${endName}.radios.0.power.use_user_power`, // CC3 uses CC1's getter (always synced)
        ],
        valueGetter: [
          `${endName}.radios.0.power.user_power`,
          `${endName}.radios.1.power.user_power`,
          `${endName}.radios.0.power.user_power`, // CC3 uses CC1's getter (always synced)
        ],
        afterOnChangeCheckbox(checked, { setValue, getValues }) {
          // Sync CC1's checkbox state to CC3 (always in same band)
          setValue(`${endName}.radios.2.power.use_user_power`, checked, {
            shouldValidate: true,
          });

          // Sync CC1's checkbox state to CC2 only if bands match
          const band1 = getValues(`${endName}.radios.0.equipment.band`);
          const band2 = getValues(`${endName}.radios.1.equipment.band`);
          if (band1 === band2) {
            setValue(`${endName}.radios.1.power.use_user_power`, checked, {
              shouldValidate: true,
            });
          }
        },
        afterOnChange(newValue, { setValue, getValues }) {
          // Sync CC1's user_power value to CC3 (always in same band)
          setValue(`${endName}.radios.2.power.user_power`, newValue, {
            shouldValidate: true,
          });

          // Sync CC1's user_power value to CC2 only if bands match
          const band1 = getValues(`${endName}.radios.0.equipment.band`);
          const band2 = getValues(`${endName}.radios.1.equipment.band`);
          if (band1 === band2) {
            setValue(`${endName}.radios.1.power.user_power`, newValue, {
              shouldValidate: true,
            });
          }
        },
        finalSync(form, getter, setter) {
          const sync = genericPowerSync(form, getter, setter);

          // Sync CC1 (radio 0) to CC3 (radio 2) - always in same band
          ['local', 'remote'].forEach((end) => {
            sync(end, 'use_user_power', 2, 0);
            sync(end, 'user_power', 2, 0);
          });

          // Sync CC1 to CC2 only if same band
          ['local', 'remote'].forEach((end) => {
            const band1 = getter(form, `${end}.radios.0.equipment.band`);
            const band2 = getter(form, `${end}.radios.1.equipment.band`);
            if (band1 === band2) {
              sync(end, 'use_user_power', 1, 0);
              sync(end, 'user_power', 1, 0);
            }
          });
        },
      },
      '2plus0': genericLkRadioGetters(endName, 2, {
        maxValueGetter: 'power.power',
        checkboxGetter: 'power.use_user_power',
        valueGetter: 'power.user_power',
      }),
      multiband: genericLkRadioGetters(endName, 3, {
        maxValueGetter: 'power.power',
        checkboxGetter: 'power.use_user_power',
        valueGetter: 'power.user_power',
      }),
      '2plus2': genericLkRadioGetters(endName, 2, {
        maxValueGetter: 'power.power',
        checkboxGetter: 'power.use_user_power',
        valueGetter: 'power.user_power',
      }),
      '4plus0': genericLkRadioGetters(endName, 4, {
        maxValueGetter: 'power.power',
        checkboxGetter: 'power.use_user_power',
        valueGetter: 'power.user_power',
      }),
      '1plus1': {
        ...maxPowerGetters_1plus1(endName),
        finalSync(form, getter, setter) {
          const sync = genericPowerSync(form, getter, setter);
          sync('local', 'use_user_power', 1, 0);
          sync('local', 'user_power', 1, 0);
          sync('local', 'use_user_power', 3, 2);
          sync('local', 'user_power', 3, 2);
          sync('remote', 'use_user_power', 2, 0);
          sync('remote', 'user_power', 2, 0);
          sync('remote', 'use_user_power', 3, 1);
          sync('remote', 'user_power', 3, 1);
        },
      },
    },
  };
}

export function genericInterference(endName: string) {
  return {
    label: 'Interference',
    component: LimitControl,
    attrName: `${endName}_interference_limit`,
    units: 'dBm',
    precision: 1,
    checkboxGetter: `${endName}.radios.0.power.use_noise`,
    valueGetter: `${endName}.radios.0.power.noise`,
    min: -144,
    max: -40,
    lk: {
      shared: false,
      carrier_1_cc_2: genericLkRadioGetters(endName, 2, {
        checkboxGetter: 'power.use_noise',
        valueGetter: 'power.noise',
      }),
      carrier_2_cc_2: genericLkRadioGetters(endName, 2, {
        checkboxGetter: 'power.use_noise',
        valueGetter: 'power.noise',
      }),
      carrier_2_cc_3: genericLkRadioGetters(endName, 3, {
        checkboxGetter: 'power.use_noise',
        valueGetter: 'power.noise',
      }),
      '2plus0': genericLkRadioGetters(endName, 2, {
        checkboxGetter: 'power.use_noise',
        valueGetter: 'power.noise',
      }),
      multiband: genericLkRadioGetters(endName, 3, {
        checkboxGetter: 'power.use_noise',
        valueGetter: 'power.noise',
      }),
      '2plus2': genericLkRadioGetters(endName, 2, {
        checkboxGetter: 'power.use_noise',
        valueGetter: 'power.noise',
      }),
      '4plus0': genericLkRadioGetters(endName, 4, {
        checkboxGetter: 'power.use_noise',
        valueGetter: 'power.noise',
      }),
      unlicensed_1plus1: {
        checkboxGetter: [
          // NOTE these are intentionally the same
          `${endName}.radios.0.power.use_noise`,
          `${endName}.radios.0.power.use_noise`,
        ],
        valueGetter: [
          // NOTE these are intentionally the same
          `${endName}.radios.0.power.noise`,
          `${endName}.radios.0.power.noise`,
        ],
      },
    },
    componentProps: {
      valueTooltip: ({ choices }) => {
        if (choices == null) {
          return null;
        }

        const bw = getEquipmentFromChoices(choices).bandwidth.choices.find(
          (c) => c.value === getEquipmentFromChoices(choices).bandwidth.value
        );
        if (bw) {
          return ` in ${bw.text} channel`;
        }
        return null;
      },
    },
  };
}

function macAddress_1plus1(endName: string) {
  if (endName === 'local') {
    return {
      getter: [
        `${endName}.radios.0.mac_address`,
        `${endName}.radios.0.mac_address`,
        `${endName}.radios.2.mac_address`,
        `${endName}.radios.2.mac_address`,
      ],
    };
  } else {
    return {
      getter: [
        `${endName}.radios.0.mac_address`,
        `${endName}.radios.1.mac_address`,
        `${endName}.radios.0.mac_address`,
        `${endName}.radios.1.mac_address`,
      ],
    };
  }
}

export function genericMacAddress(endName: string) {
  return {
    label: 'MAC Address',
    attrName: `${endName}_mac_address`,
    getter: `${endName}.radios.0.mac_address`,
    component: GenericScaledField,
    usePrefs: 'macAddressFormat',
    lk: {
      shared: false,
      unlicensed_1plus1: genericLkRadioGetters(endName, 2, {
        getter: 'mac_address',
      }),
      '2plus0': genericLkRadioGetters(endName, 2, {
        getter: 'mac_address',
      }),
      multiband: genericLkRadioGetters(endName, 3, {
        getter: 'mac_address',
      }),
      '2plus2': genericLkRadioGetters(endName, 2, {
        getter: 'mac_address',
      }),
      '4plus0': genericLkRadioGetters(endName, 4, {
        getter: 'mac_address',
      }),
      '1plus1': {
        ...macAddress_1plus1(endName),
        finalSync(form, getter, setter) {
          setter(
            form,
            'local.radios.1.mac_address',
            getter(form, 'local.radios.0.mac_address')
          );
          setter(
            form,
            'local.radios.3.mac_address',
            getter(form, 'local.radios.2.mac_address')
          );
          setter(
            form,
            'remote.radios.2.mac_address',
            getter(form, 'remote.radios.0.mac_address')
          );
          setter(
            form,
            'remote.radios.3.mac_address',
            getter(form, 'remote.radios.1.mac_address')
          );
        },
      },
    },
  };
}

export function makeAntennaNextValue(endName: string) {
  return (currentValue: string, newChoices: PTPChoices) => {
    const antennaChoices = filterAndSortAntennas(
      getAntennasFromChoices(newChoices, endName)
    );
    const choiceIds = antennaChoices.map((a) => a.id);
    const payload = store.getState().antenna.antennaPayload;
    if (
      payload != null &&
      payload[endName] &&
      choiceIds.includes(payload[endName].id)
    ) {
      // we need to reset the csutom antenna data in redux after selecting the
      // antenna
      store.dispatch(reset());
      return payload[endName].id;
    }
    if (choiceIds.includes(currentValue)) {
      return currentValue;
    } else {
      const defaultAntenna = antennaChoices.filter((a) => a.data.is_default);
      if (defaultAntenna.length > 0) {
        return defaultAntenna[0].id;
      }
      return choiceIds[0];
    }
  };
}
